import React, { useEffect, useState, useCallback, useRef } from 'react';
import { initializeItinerarySocket, processItinerarySteps, findCurrentWaypoint, sendStartBreak, sendRequestSplitShift, sendPayment, sendComplete } from './itinerarySocketHelper';
import ItineraryItinerary from './ItineraryItinerary';
import FlagTripCreateModal from './FlagTripCreateModal';
import ItineraryMap from './ItineraryMap';
import ItineraryCurrentWaypoint from './ItineraryCurrentWaypoint';
import PaymentCollectionSheet from '../LiveDispatch/PaymentCollectionSheet';
import usePassengerOtpDialogue from '../LiveDispatch/usePassengerOtpDialogue';
import ItineraryFinalFareModal from './ItineraryFinalFareModal';
import { resolveTripPaymentStatus, tripIdKey, mergePaymentOverride, TRIP_PAYMENT_STATE } from '../../../utils/tripPaymentStatus';
import { receiptPaymentMethod } from '../../../utils/tripPaymentSummary';
import { isMarketplaceAvailable } from '../../../utils/onDemandOffer';
import GeolocationHandler from '../LiveDispatch/GeolocationHandler';
import comms from '../../comms';

function ItineraryDispatch({
  socket: passedSocket,
  fleetSettings,
  userSettings,
  driverData,
  navigateTo,
  routeDetails: initialRouteDetails,
  selectedVehicle,
  onBack,
  isDispatchMode = false
}) {
  const [googleApiKey, setGoogleApiKey] = useState(null);
  const [map, setMap] = useState(null);
  const [isAccurate, setIsAccurate] = useState(false);
  // Use routeDetails startLat/startLon as initial center if available, otherwise use default
  const getInitialCenter = () => {
    if (initialRouteDetails?.startLat && initialRouteDetails?.startLon) {
      return { lat: initialRouteDetails.startLat, lng: initialRouteDetails.startLon };
    }
    // Use fleet config GPS if available, otherwise fall back to generic US center
    if (fleetSettings?.centerLat && fleetSettings?.centerLon) {
      return { lat: fleetSettings.centerLat, lng: fleetSettings.centerLon };
    }
    return { lat: 39.8283, lng: -98.5795 };
  };
  const [center, setCenter] = useState(getInitialCenter);
  const [loading, setLoading] = useState(true);
  const [isPhone, setIsPhone] = useState(false);
  const [vehiclePosition, setVehiclePosition] = useState(null);
  
  // Route state - managed by socket updates
  const [routeDetails, setRouteDetails] = useState(initialRouteDetails);
  const [currentWaypoint, setCurrentWaypoint] = useState(null);
  const [schedule, setSchedule] = useState([]);
  
  // FEB 2026: Sync routeDetails state when initialRouteDetails prop changes (e.g., re-authentication)
  // This fixes the bug where stale route data from yesterday is used after driver re-logs in
  useEffect(() => {
    if (initialRouteDetails?.routeId && initialRouteDetails.routeId !== routeDetails?.routeId) {
      console.log('[ItineraryDispatch] Route changed from props, updating state:', 
        routeDetails?.routeId, '->', initialRouteDetails.routeId);
      setRouteDetails(initialRouteDetails);
      // Clear any stale persisted data to prevent future mismatches
      comms.clearPersistedRouteData();
    }
  }, [initialRouteDetails?.routeId]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Active break state - tracks if driver is currently on break (even if break is not currentWaypoint)
  const [activeBreak, setActiveBreak] = useState(null);

  // Phase B — itinerary-mode flag trip modal
  const [showFlagTripModal, setShowFlagTripModal] = useState(false);

  // This component only ever renders in itinerary mode, so the dispatch type is known.
  // It still goes through the shared helper rather than hard-coding `true`, so the
  // Marketplace rule stays defined in exactly one place (utils/onDemandOffer) for the
  // Dashboard tile, the App route guard and this entry point alike.
  const marketplaceAvailable = isMarketplaceAvailable('itinerary');
  
  // Itinerary change tracking for toast notifications
  const [itineraryChangeToast, setItineraryChangeToast] = useState(null);
  const itinerarySnapshotRef = useRef(new Map()); // Map of StepId -> { tripNo, jobNo, stepType }
  const lastActionSourceRef = useRef(null); // Track if last action was from this client
  
  // Connection status state for toast display
  const [connectionError, setConnectionError] = useState(null);
  
  // Refs for GPS timing and map reference
  const lastLocationRef = useRef(null);
  const lastServerUpdateRef = useRef(0);
  const mapContainerRef = useRef(null);
  
  // Track whether we're on a phone vs tablet/desktop
  // Respect fleet setting 'displayOrientation' to force portrait or landscape layout
  useEffect(() => {
    const handleResize = () => {
      // Check if fleet setting 'displayOrientation' is set
      if (fleetSettings?.displayOrientation === 'portrait') {
        setIsPhone(true); // Force portrait layout
      } else if (fleetSettings?.displayOrientation === 'landscape') {
        setIsPhone(false); // Force landscape layout
      } else {
        // Default responsive behavior based on window width
        setIsPhone(window.innerWidth < 768);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [fleetSettings?.displayOrientation]);
  
  // Load Google Maps API key from fleetSettings (like FixedShuttlebus does)
  useEffect(() => {
    if (fleetSettings && fleetSettings.googleApiKey) {
      setGoogleApiKey(fleetSettings.googleApiKey);
      setLoading(false);
    }
  }, [fleetSettings]);
  
  // Initialize itinerary snapshot from initial route details
  // This allows us to detect changes in subsequent routeUpdate messages
  useEffect(() => {
    if (initialRouteDetails?.itinerary && itinerarySnapshotRef.current.size === 0) {
      const initialSnapshot = new Map();
      initialRouteDetails.itinerary.forEach(step => {
        const key = step.StepId || step.stepId;
        if (key) {
          initialSnapshot.set(key, {
            tripNo: step.tripNo || step.TripNo,
            jobNo: step.JobNo || step.jobNo,
            stepType: step.type || step.StepType
          });
        }
      });
      itinerarySnapshotRef.current = initialSnapshot;
      console.log('Initialized itinerary snapshot with', initialSnapshot.size, 'steps');
    }
  }, [initialRouteDetails]);
  
  // Subscribe to connection status changes for reconnection handling
  useEffect(() => {
    let wasDisconnected = false;
    
    const unsubscribe = comms.onConnectionStatusChange((status, previousStatus) => {
      console.log('ItineraryDispatch connection status:', status, 'previous:', previousStatus);
      
      // Track disconnection
      if (status === 'disconnected' || status === 'error') {
        wasDisconnected = true;
        setConnectionError(null); // Clear any previous error
      }
      
      // Show error toast on failed status
      if (status === 'failed') {
        setConnectionError('Connection failed. Tap the status icon to reconnect.');
      }
      
      // Clear error on successful connection
      if (status === 'connected') {
        setConnectionError(null);
        
        // Reload route data after reconnection
        if (wasDisconnected && routeDetails?.routeId) {
          console.log('Reconnected - reloading itinerary data...');
          comms.getRouteDetails(routeDetails.routeId)
            .then((updatedRouteData) => {
              console.log('Route data reloaded after reconnection:', updatedRouteData);
              if (updatedRouteData) {
                setRouteDetails(updatedRouteData);
              }
            })
            .catch((err) => {
              console.error('Failed to reload route data after reconnection:', err);
            });
        }
        wasDisconnected = false;
      }
    });
    
    return unsubscribe;
  }, [routeDetails?.routeId]);
  
  // Initialize socket listeners for itinerary updates
  useEffect(() => {
    if (!passedSocket) {
      console.warn('Socket not provided to ItineraryDispatch - real-time updates disabled');
      return;
    }
    
    console.log('Initializing itinerary socket listeners...');
    
    // Set active route for centralized room management
    if (initialRouteDetails?.routeId) {
      comms.setActiveRoute(initialRouteDetails.routeId);
    }
    
    const cleanup = initializeItinerarySocket(passedSocket, (updatedRouteData, metadata = {}) => {
      console.log('ItineraryDispatch received route update:', updatedRouteData, 'metadata:', metadata);
      
      // Check if this update came from cross-room broadcaster (dispatch console)
      const isExternalUpdate = metadata.isExternal === true;
      
      console.log('Route update - source:', metadata.source, 'isExternal:', isExternalUpdate);
      
      // Track itinerary changes for toast notification - ONLY for external updates from cross-room broadcaster
      if (isExternalUpdate && updatedRouteData?.itinerary && itinerarySnapshotRef.current.size > 0) {
        const newSnapshot = new Map();
        const changes = { added: [], removed: [], updated: false };
        
        // Build new snapshot and detect additions
        updatedRouteData.itinerary.forEach(step => {
          const key = step.StepId || step.stepId;
          const stepInfo = {
            tripNo: step.tripNo || step.TripNo,
            jobNo: step.JobNo || step.jobNo,
            stepType: step.type || step.StepType
          };
          newSnapshot.set(key, stepInfo);
          
          if (!itinerarySnapshotRef.current.has(key)) {
            changes.added.push(stepInfo);
          }
        });
        
        // Detect removals
        itinerarySnapshotRef.current.forEach((stepInfo, key) => {
          if (!newSnapshot.has(key)) {
            changes.removed.push(stepInfo);
          }
        });
        
        // Update snapshot
        itinerarySnapshotRef.current = newSnapshot;
        
        // Show toast if there are changes (added/removed trips)
        const totalChanges = changes.added.length + changes.removed.length;
        if (totalChanges > 0) {
          setItineraryChangeToast(prev => ({
            added: (prev?.added || 0) + changes.added.length,
            removed: (prev?.removed || 0) + changes.removed.length
          }));
        } else {
          // No trips added/removed, but still an external update - show generic update toast
          // This covers time changes, reordering, etc.
          console.log('External route update received with no trip changes - showing update toast');
          setItineraryChangeToast({ updated: true, added: 0, removed: 0 });
        }
      } else if (updatedRouteData?.itinerary) {
        // First load or self-initiated update - just build snapshot
        const newSnapshot = new Map();
        updatedRouteData.itinerary.forEach(step => {
          const key = step.StepId || step.stepId;
          newSnapshot.set(key, {
            tripNo: step.tripNo || step.TripNo,
            jobNo: step.JobNo || step.jobNo,
            stepType: step.type || step.StepType
          });
        });
        itinerarySnapshotRef.current = newSnapshot;
      }
      
      // Check for active break in updated data
      // Note: Check multiple fields since raw itinerary uses StepDescription/StepType/breakStep, 
      // while processed steps use type
      const breakStep = updatedRouteData?.itinerary?.find(step => {
        const stepType = (step.type || step.StepType || step.StepDescription || '').toLowerCase();
        const isBreakStep = stepType === 'break' || step.breakStep === true;
        const isArrived = step.StepStatus === 'arrived' || step.isArrived === true;
        return isBreakStep && isArrived && !step.isCompleted;
      });
      // Enrich breakStep with vehicleId and routeId if found
      if (breakStep) {
        // Use fallback chain for vehicleId
        const vehicleId = selectedVehicle?.Id 
          || selectedVehicle?.id 
          || driverData?.dispatchData?.vehicleId
          || driverData?.dispatchData?.vehicleObject?.id
          || driverData?.dispatchData?.vehicleObject?.Id;
        setActiveBreak({
          ...breakStep,
          vehicleId: vehicleId,
          routeId: updatedRouteData.routeId
        });
      } else {
        setActiveBreak(null);
      }
      
      // Check for split shift approval notification
      const approvedBreak = updatedRouteData?.itinerary?.find(step => {
        const stepType = (step.type || '').toLowerCase();
        return stepType === 'break' && step.StepStatus === 'splitShiftApproved';
      });
      if (approvedBreak) {
        // Show approval toast
        setConnectionError('Split shift request approved! You can now clock out.');
        setTimeout(() => setConnectionError(null), 8000);
      }
      
      // Check for split shift rejection notification
      const rejectedBreak = updatedRouteData?.itinerary?.find(step => {
        const stepType = (step.type || '').toLowerCase();
        return stepType === 'break' && step.StepStatus === 'splitShiftRejected';
      });
      if (rejectedBreak) {
        // Show rejection toast
        setConnectionError('Split shift request was declined');
        setTimeout(() => setConnectionError(null), 5000);
      }
      
      setRouteDetails(updatedRouteData);
    });
    
    return cleanup;
  }, [passedSocket, initialRouteDetails?.routeId]);
  
  // Helper function to get vehicleId with fallback chain
  // This ensures we always have a vehicleId even if selectedVehicle prop is empty
  const getVehicleId = useCallback(() => {
    return selectedVehicle?.Id 
      || selectedVehicle?.id 
      || driverData?.dispatchData?.vehicleId
      || driverData?.dispatchData?.vehicleObject?.id
      || driverData?.dispatchData?.vehicleObject?.Id
      || null;
  }, [selectedVehicle, driverData]);

  // ── Live payment settlements ────────────────────────────────────────────────
  // trip:paymentSettled is the server's authoritative real-time "Paid" signal
  // (PAYMENT_DRIVERAPP_HANDOFF.md). Subscribing here lets the waypoint card's
  // Paid/Unpaid badge flip the moment a passenger pays, without waiting for a route
  // reload. Listened to directly on the socket rather than through comms'
  // onTripPaymentSettled callback slot, which LiveDispatch already owns.
  //
  // Everything below is keyed by tripIdKey(reservation.id) — the AutoDispatch Trip.Id —
  // because the server's pushes carry only that id, and a route can hold several active
  // trips: a settlement for one must never mark another Paid.
  const [paymentOverrides, setPaymentOverrides] = useState({});
  const [reconcileTripIds, setReconcileTripIds] = useState(() => new Set());
  // Collect Payment sheet (shared with Live Dispatch). source: 'endTrip' = opened from
  // Complete at the drop-off; completion is sent from the sheet once the trip is Paid.
  const [paymentSheet, setPaymentSheet] = useState({ open: false, tripId: null, source: 'endTrip' });
  // Authoritative summaries fetched from /API/Payment/active-trips when the sheet opens.
  const [refreshedPayments, setRefreshedPayments] = useState({});
  const [collectionState, setCollectionState] = useState({ tripId: null, url: null, error: null });
  const [cashState, setCashState] = useState({ pending: false, error: null });
  const [receiptState, setReceiptState] = useState({ loading: false, url: null, error: null, messageSid: null, sentTo: null });

  const applySettlement = useCallback((tripId, info = {}) => {
    const k = tripIdKey(tripId);
    if (!k) return;
    if (info.needsReconciliation) {
      setReconcileTripIds((prev) => { const next = new Set(prev); next.add(k); return next; });
      return;
    }
    setPaymentOverrides((prev) => ({
      ...prev,
      [k]: {
        ...(prev[k] || {}),
        paid: true,
        paymentAmount: Number.isFinite(Number(info.paymentAmount)) ? Number(info.paymentAmount) : prev[k]?.paymentAmount,
        paymentMethod: info.paymentMethod || prev[k]?.paymentMethod || 'Credit Card',
        transactionId: info.transactionId || prev[k]?.transactionId || null,
      },
    }));
  }, []);

  useEffect(() => {
    const socket = passedSocket || comms.getSocket?.();
    if (!socket) return undefined;
    // Authoritative Paid push (HBSS webhook, RiderApp QR payment, wallet). Applied
    // immediately even while the trip is Loaded; completion stays on the normal step.
    const onSettled = (data) => {
      if (data?.tripId) applySettlement(data.tripId, data);
    };
    // Hosted card page link — "link created", never paid.
    const onCollectionUrl = (data) => {
      if (!data) return;
      setCollectionState({ tripId: data.tripId || null, url: data.url || null, error: data.error || null });
    };
    const onCollectionResult = (data) => {
      if (!data) return;
      if (data.success === true) {
        applySettlement(data.tripId, { paymentMethod: 'Credit Card', transactionId: data.transactionId, paymentAmount: data.paymentAmount });
      } else if (data.success === false) {
        setCollectionState({
          tripId: data.tripId || null,
          url: null,
          error: data.error || (data.status === 'Expired' ? 'Collection link expired — try again.' : 'Payment was not completed.'),
        });
      }
    };
    const onReceipt = (data) => {
      if (!data) return;
      if (data.success) {
        setReceiptState((prev) => ({
          loading: false,
          url: data.receiptUrl || null,
          error: null,
          messageSid: data.messageSid || null,
          sentTo: data.messageSid ? prev.sentTo : null,
        }));
      } else {
        setReceiptState((prev) => ({ ...prev, loading: false, error: data.error || data.message || 'Receipt request failed.' }));
      }
    };
    socket.on('trip:paymentSettled', onSettled);
    socket.on('tripCollectionUrl', onCollectionUrl);
    socket.on('tripCollectionResult', onCollectionResult);
    socket.on('tripReceiptResult', onReceipt);
    return () => {
      socket.off('trip:paymentSettled', onSettled);
      socket.off('tripCollectionUrl', onCollectionUrl);
      socket.off('tripCollectionResult', onCollectionResult);
      socket.off('tripReceiptResult', onReceipt);
    };
  }, [passedSocket, applySettlement]);

  // Trip id on an itinerary waypoint appears under a few names depending on how the
  // step was built; reservation.id is the authoritative one in formatted data.
  const waypointTripId = (wp) => wp?.trip?.reservation?.id || wp?.TripId || wp?.tripId || null;
  const currentTripId = waypointTripId(currentWaypoint);
  const currentOverride = currentTripId ? paymentOverrides[tripIdKey(currentTripId)] || null : null;
  const isCurrentTripPaidLive = !!currentOverride?.paid;
  const currentNeedsReview = !!currentTripId && reconcileTripIds.has(tripIdKey(currentTripId));

  // Payment block for the sheet: reservation, then the refreshed server summary, then
  // the live override — the same precedence the waypoint badge uses.
  const paymentBlockFor = (wp) => {
    const id = waypointTripId(wp);
    const k = tripIdKey(id);
    const reservation = wp?.trip?.reservation || null;
    const refreshed = k ? refreshedPayments[k] : null;
    let base = reservation;
    if (refreshed) {
      const fetchedFare = Number(refreshed.fare);
      base = {
        ...(reservation || {}),
        ...refreshed,
        paymentAmount: fetchedFare > 0 ? fetchedFare : (reservation?.paymentAmount ?? reservation?.price ?? null),
      };
    }
    return mergePaymentOverride(base, k ? paymentOverrides[k] : null);
  };

  const paymentStatusFor = (wp) => {
    const k = tripIdKey(waypointTripId(wp));
    return resolveTripPaymentStatus(paymentBlockFor(wp), {
      settledLive: !!(k && paymentOverrides[k]?.paid),
      needsReconciliation: !!(k && reconcileTripIds.has(k)),
    });
  };

  const refreshPaymentState = async (wp) => {
    const id = waypointTripId(wp);
    if (!id) return null;
    const summary = await comms.getTripPaymentState({ tripId: id });
    if (summary) setRefreshedPayments((prev) => ({ ...prev, [tripIdKey(id)]: summary }));
    return summary;
  };

  // ── Metered Itinerary trips: PRICE → PRICE|Ok before any collection ────────
  // Itinerary has no soft meter, so the driver enters the metered total in
  // ItineraryFinalFareModal; it becomes the trip's meter price row and is sent as
  // PRICE|<TripNo>|items. Only after dispatch's PRICE|Ok (comms.finalizePrice —
  // never falls through to complete) does the Collect Payment sheet open. Metered
  // account/no-charge trips still need PRICE (server PRICE_REQUIRED guard) and then
  // complete directly.
  const [fareFinalize, setFareFinalize] = useState({ open: false, tripId: null, source: 'endTrip', status: 'idle', error: null });

  const needsFareFinalize = (wp) => {
    const reservation = wp?.trip?.reservation;
    if (!reservation || reservation.metered !== true) return false;
    const block = paymentBlockFor(wp);
    return !(block && block.priceFinal === true);
  };

  const meterPriceItemsFor = (wp, total) => {
    const reservation = wp?.trip?.reservation || {};
    const rows = Array.isArray(reservation.priceItems) ? reservation.priceItems : [];
    const meterRow = (reservation.meter && reservation.meter.id)
      ? { id: reservation.meter.id }
      : rows.find((p) => String(p?.type || p?.RateItemType || '').toLowerCase().indexOf('meter') > -1 && (p.id || p.Id));
    if (!meterRow) return null;
    return [{ id: meterRow.id || meterRow.Id, rate: total, name: 'Meter', type: 'meter' }];
  };

  const handleFinalizeFare = async (total) => {
    const wp = fareFinalizeWaypoint;
    if (!wp) return;
    const id = waypointTripId(wp);
    const reservation = wp.trip?.reservation || {};
    const tripNo = reservation.number || wp.tripNo || wp.TripNo;
    const items = meterPriceItemsFor(wp, total);
    if (!tripNo) {
      setFareFinalize((prev) => ({ ...prev, status: 'error', error: 'This trip has no trip number to price. Reload the route and try again.' }));
      return;
    }
    if (!items) {
      setFareFinalize((prev) => ({ ...prev, status: 'error', error: 'This trip has no meter rate item, so its fare cannot be finalized here. Contact dispatch.' }));
      return;
    }
    setFareFinalize((prev) => ({ ...prev, status: 'pending', error: null }));
    try {
      await comms.finalizePrice(tripNo, items);
    } catch (err) {
      setFareFinalize((prev) => ({ ...prev, status: 'error', error: err?.message || 'Dispatch did not confirm the fare. Please retry.' }));
      return;
    }
    // Accepted: the trip is now final at the confirmed amount until the
    // authoritative refresh replaces it.
    const k = tripIdKey(id);
    const summary = await refreshPaymentState(wp);
    const finalAmount = Number(summary?.fare) > 0 ? Number(summary.fare) : total;
    setPaymentOverrides((prev) => ({
      ...prev,
      [k]: { ...(prev[k] || {}), priceFinal: true, paymentAmount: finalAmount },
    }));
    const block = mergePaymentOverride(
      { ...reservation, ...(summary || {}), priceFinal: true, paymentAmount: finalAmount, price: finalAmount },
      { ...(paymentOverrides[k] || {}), priceFinal: true, paymentAmount: finalAmount }
    );
    const status = resolveTripPaymentStatus(block, {
      settledLive: !!paymentOverrides[k]?.paid,
      needsReconciliation: reconcileTripIds.has(k),
    });
    const source = fareFinalize.source;
    setFareFinalize({ open: false, tripId: null, source: 'endTrip', status: 'idle', error: null });
    if (status.state === TRIP_PAYMENT_STATE.PAID ||
        status.state === TRIP_PAYMENT_STATE.ACCOUNT_BILLED ||
        status.state === TRIP_PAYMENT_STATE.NO_CHARGE) {
      // Nothing to collect; the fare is final so the itinerary completion guard passes.
      if (source === 'endTrip') sendComplete(sheetTripData(wp));
      return;
    }
    setCollectionState({ tripId: null, url: null, error: null });
    setCashState({ pending: false, error: null });
    setReceiptState({ loading: false, url: null, error: null, messageSid: null, sentTo: null });
    setPaymentSheet({ open: true, tripId: id, source });
  };

  const closeFareFinalize = () => setFareFinalize({ open: false, tripId: null, source: 'endTrip', status: 'idle', error: null });

  const openPaymentSheet = (wp, source = 'endTrip') => {
    const id = waypointTripId(wp);
    if (!id) return;
    if (needsFareFinalize(wp)) {
      setFareFinalize({ open: true, tripId: id, source, status: 'idle', error: null });
      refreshPaymentState(wp);
      return;
    }
    setCollectionState({ tripId: null, url: null, error: null });
    setCashState({ pending: false, error: null });
    setReceiptState({ loading: false, url: null, error: null, messageSid: null, sentTo: null });
    setPaymentSheet({ open: true, tripId: id, source });
    refreshPaymentState(wp);
  };
  const closePaymentSheet = () => setPaymentSheet({ open: false, tripId: null, source: 'endTrip' });

  // The sheet follows the current waypoint; if the route moves on, it closes.
  const sheetWaypoint =
    paymentSheet.open && currentTripId && tripIdKey(currentTripId) === tripIdKey(paymentSheet.tripId)
      ? currentWaypoint
      : null;
  const fareFinalizeWaypoint =
    fareFinalize.open && currentTripId && tripIdKey(currentTripId) === tripIdKey(fareFinalize.tripId)
      ? currentWaypoint
      : null;
  const sheetTripData = (wp) => ({
    tripId: wp.TripId || waypointTripId(wp),
    vehicleId: wp.vehicleId || getVehicleId(),
    routeId: wp.routeId || routeDetails?.routeId,
  });

  // Cash / account: the acknowledged itinerary `payment` action (never Live's EPMTM).
  // Nothing is marked paid until itinerary_ack { action:'payment' } arrives.
  const handleCollectCash = async ({ amount }) => {
    if (!sheetWaypoint) return;
    const id = waypointTripId(sheetWaypoint);
    setCashState({ pending: true, error: null });
    try {
      const ack = await sendPayment(sheetTripData(sheetWaypoint), 'CASH', amount);
      applySettlement(id, { paymentMethod: 'Cash', paymentAmount: ack?.amount ?? amount });
      setCashState({ pending: false, error: null });
    } catch (err) {
      setCashState({ pending: false, error: err?.message || 'Cash payment was not recorded.' });
    }
  };

  // ── Assisted card collection: verify the passenger, then collect ─────────
  // Identical dialogue to Live Dispatch (one shared hook); only the trip plumbing
  // and the itinerary transports differ.
  const [verifiedTripId, setVerifiedTripId] = useState(null);
  const selectedFareRef = useRef(null);

  const handleSelectFare = (amounts) => { selectedFareRef.current = amounts; };

  const handlePassengerVerified = () => {
    if (!sheetWaypoint) return;
    setVerifiedTripId(waypointTripId(sheetWaypoint));
    const amounts = selectedFareRef.current;
    if (amounts) {
      handleRequestHostedPage(amounts);
      return;
    }
    // Unreachable in the normal flow (the sheet reports the fare before verification
    // starts), but an explicit error beats an unresolvable waiting screen.
    setCollectionState({
      tripId: waypointTripId(sheetWaypoint),
      url: null,
      error: 'The fare for this payment was lost. Go back and choose the amount again.',
    });
  };

  // Terminal payment errors (already paid, pending elsewhere) end the dialogue and
  // re-read authoritative state rather than letting the driver retry the code.
  const handleOtpPaymentStateChanged = () => {
    if (sheetWaypoint) refreshPaymentState(sheetWaypoint);
  };

  const otpDialogue = usePassengerOtpDialogue({
    tripId: sheetWaypoint ? waypointTripId(sheetWaypoint) : null,
    otpCapability: sheetWaypoint ? paymentBlockFor(sheetWaypoint)?.otp : null,
    onVerified: handlePassengerVerified,
    onPaymentStateChanged: handleOtpPaymentStateChanged,
  });

  // Manual card: fare/tip chosen on the sheet; AutoDispatch creates the HBSS page.
  const handleRequestHostedPage = ({ fareAmount, tipAmount, totalAmount }) => {
    if (!sheetWaypoint) return;
    const id = waypointTripId(sheetWaypoint);
    setCollectionState({ tripId: id, url: null, error: null });
    const sent = comms.requestTripCollection({
      vehicleId: sheetWaypoint.vehicleId || getVehicleId(),
      tripId: id,
      fareAmount,
      tipAmount,
      totalAmount,
      presentation: 'embedded',
    });
    if (!sent) setCollectionState({ tripId: id, url: null, error: 'Not connected to dispatch. Reconnect and try again.' });
  };

  const receiptMethodFor = (wp) => receiptPaymentMethod(
    paymentOverrides[tripIdKey(waypointTripId(wp))]?.paymentMethod || wp?.trip?.reservation?.paymentMethod
  );
  const emptyReceipt = { loading: false, url: null, error: null, messageSid: null, sentTo: null };
  const handleViewReceipt = () => {
    if (!sheetWaypoint) return;
    setReceiptState({ ...emptyReceipt, loading: true });
    if (!comms.getTripReceipt({ tripId: waypointTripId(sheetWaypoint), paymentMethod: receiptMethodFor(sheetWaypoint) })) {
      setReceiptState({ ...emptyReceipt, error: 'Not connected to dispatch. Reconnect and try again.' });
    }
  };
  const handleTextReceipt = (mobileNumber) => {
    if (!sheetWaypoint) return;
    setReceiptState({ ...emptyReceipt, loading: true, sentTo: mobileNumber });
    if (!comms.sendTripReceiptSms({ tripId: waypointTripId(sheetWaypoint), mobileNumber, paymentMethod: receiptMethodFor(sheetWaypoint) })) {
      setReceiptState({ ...emptyReceipt, error: 'Not connected to dispatch. Reconnect and try again.' });
    }
  };

  // Paid and arrived: the Itinerary completion contract — ITINERARY_ACTION complete.
  const handleCompleteFromSheet = () => {
    if (!sheetWaypoint) return;
    const tripData = sheetTripData(sheetWaypoint);
    closePaymentSheet();
    sendComplete(tripData);
  };

  const collectionForSheet =
    !collectionState.tripId ||
    (!!sheetWaypoint && tripIdKey(collectionState.tripId) === tripIdKey(waypointTripId(sheetWaypoint)));

  // ── Phase B flag-trip gate (client-side; server independently re-checks) ─────
  // Show the Flag button only when: fleet flag on, no passenger on board, not on
  // break, and the next scheduled pickup is ≥ 15 min away (or none).
  const lc = (s) => String(s || '').toLowerCase();
  const hasLoadedTrip = !!currentWaypoint &&
    (lc(currentWaypoint.type) === 'delivery' || lc(currentWaypoint.type) === 'dropoff');
  const onBreak = !!activeBreak;
  const nextPickupStep = (schedule || []).find(
    (s) => lc(s.type || s.StepDescription) === 'pickup' && !s.isCompleted
  );
  const nextPickupRaw = nextPickupStep
    ? (nextPickupStep.StartDateTime || nextPickupStep.eta || nextPickupStep.trip?.pickup?.date)
    : null;
  const minutesUntilNextPickup = nextPickupRaw
    ? Math.round((new Date(nextPickupRaw).getTime() - Date.now()) / 60000)
    : Infinity;
  // TEMP (testing): force-enable the Phase B flag-trip button for the ACET fleet,
  // regardless of the fleetSettings.flagTrip value, so it shows on ACET. Other
  // fleets still honour fleetSettings.flagTrip. NOTE: the server independently
  // enforces its own flagTrip on rates/quote/create.
  const fc = String(fleetSettings?.unitId || localStorage.getItem('fleetCode') || '').toUpperCase();
  const fu = String(fleetSettings?.fleetUrl || '').toLowerCase();
  const isAcetFleet = fc.includes('ACET') || fu.includes('acet');
  const flagTripEnabled = fleetSettings?.flagTrip === true || isAcetFleet;
  const canFlag =
    flagTripEnabled &&
    !hasLoadedTrip &&
    !onBreak &&
    minutesUntilNextPickup >= 15;
  const openFlagTrip = useCallback(() => setShowFlagTripModal(true), []);

  // Process itinerary and find current waypoint when routeDetails changes
  useEffect(() => {
    if (!routeDetails || !routeDetails.itinerary || !routeDetails.trips) {
      setSchedule([]);
      setCurrentWaypoint(null);
      return;
    }
    
    console.log('[ItineraryDispatch] Processing routeDetails update, itinerary steps:', routeDetails.itinerary?.length);
    console.log('[ItineraryDispatch] First 3 itinerary steps isCompleted:', 
      routeDetails.itinerary?.slice(0, 3).map(s => ({ 
        stepType: s.StepDescription, 
        isCompleted: s.isCompleted,
        _optimistic: s._optimistic 
      }))
    );
    
    // Update active route if routeId changed
    if (routeDetails.routeId) {
      comms.setActiveRoute(routeDetails.routeId);
    }
    
    // Get vehicleId with fallback chain
    const vehicleId = getVehicleId();
    console.log('[ItineraryDispatch] Using vehicleId:', vehicleId);
    
    // Process itinerary steps (smart filtering - keeps completed steps appearing after incomplete ones)
    const processed = processItinerarySteps(routeDetails.itinerary, routeDetails.trips);
    
    // Add vehicleId and routeId to each step
    const enriched = processed.map(step => ({
      ...step,
      vehicleId: vehicleId,
      routeId: routeDetails.routeId
    }));

    //filter out steps assocuiated with cancelled trips
    const filtered = enriched.filter(step => {
      if (step.tripId) {
        const trip = routeDetails.trips.find(t => t.Id === step.tripId || t.id === step.tripId);  
        if (trip && trip.Status && trip.Status.toLowerCase() === 'cancelled') {
          return false; // Exclude cancelled trip steps
        }
      }
      return true;
    });
    
    setSchedule(filtered);
    
    // Find current waypoint (first INCOMPLETE step, skips completed ones)
    const current = findCurrentWaypoint(enriched);
    console.log('[ItineraryDispatch] Current waypoint:', current ? {
      type: current.type,
      tripId: current.TripId,
      isCompleted: current.isCompleted
    } : 'null (all completed)');
    setCurrentWaypoint(current);
    
    // Check for active break in itinerary data
    // Note: Check multiple fields since raw itinerary uses StepDescription/StepType/breakStep
    const breakStep = routeDetails.itinerary.find(step => {
      const stepType = (step.type || step.StepType || step.StepDescription || '').toLowerCase();
      const isBreakStep = stepType === 'break' || step.breakStep === true;
      const isArrived = step.StepStatus === 'arrived' || step.isArrived === true;
      return isBreakStep && isArrived && !step.isCompleted;
    });
    // Enrich breakStep with vehicleId and routeId if found
    if (breakStep) {
      setActiveBreak({
        ...breakStep,
        vehicleId: vehicleId,
        routeId: routeDetails.routeId
      });
    } else {
      setActiveBreak(null);
    }
    
    // If no incomplete steps remain, navigate to CompleteShift
    if (!current && enriched.length === 0) {
      console.log('All itinerary steps completed - navigating to CompleteShift');
      if (typeof navigateTo === 'function') {
        navigateTo('completeShift');
      }
    }
  }, [routeDetails, getVehicleId, navigateTo]);
  
  // Send GPS update to server (throttled to every 30 seconds)
  const sendGPSUpdateToServer = useCallback((location) => {
    const now = Date.now();
    const timeSinceLastUpdate = now - lastServerUpdateRef.current;
    
    // Only send to server if 30 seconds have passed since last update
    if (timeSinceLastUpdate >= 30000) {
      const vehicleId = getVehicleId();
      if (vehicleId && routeDetails && routeDetails.routeId) {
        console.log('ItineraryDispatch - Sending GPS_UPDATE to server:', {
          lat: location.lat,
          lng: location.lng,
          vehicleId: vehicleId,
          routeId: routeDetails.routeId
        });
        
        comms.sendGPSUpdate(
          location.lat,
          location.lng,
          location.speed ?? 0, // speed (mph)

          vehicleId,
          routeDetails.routeId,
          'route',
          null,
          selectedVehicle
        );
        
        lastServerUpdateRef.current = now;
      }
    }
  }, [getVehicleId, routeDetails, selectedVehicle]);
  
  const handleLocationUpdate = (newLocation) => {
    // Store the latest location
    lastLocationRef.current = newLocation;
    
    // Immediately update components for real-time functionality
    setCenter(newLocation);
    setVehiclePosition(newLocation);
    if (map) {
      map.panTo(newLocation);
    }
    
    // Send GPS update to server (throttled)
    sendGPSUpdateToServer(newLocation);
  };
  
  const onLoad = useCallback((loadedMap) => {
    setMap(loadedMap);
  }, []);
  
  // Handle starting a break from BreakModal
  const handleStartBreak = useCallback((breakStep) => {
    if (!breakStep || !vehiclePosition) {
      console.error('Cannot start break - missing step or location');
      return;
    }
    
    sendStartBreak(
      {
        StepId: breakStep.StepId || breakStep.stepId,
        vehicleId: breakStep.vehicleId || getVehicleId(),
        routeId: breakStep.routeId || routeDetails?.routeId
      },
      vehiclePosition
    );
  }, [vehiclePosition, getVehicleId, routeDetails]);
  
  // Handle requesting a split shift from BreakModal
  const handleRequestSplitShift = useCallback((breakStep) => {
    if (!breakStep || !vehiclePosition) {
      console.error('Cannot request split shift - missing step or location');
      return;
    }
    
    sendRequestSplitShift(
      {
        StepId: breakStep.StepId || breakStep.stepId,
        vehicleId: breakStep.vehicleId || getVehicleId(),
        routeId: breakStep.routeId || routeDetails?.routeId
      },
      vehiclePosition
    );
  }, [vehiclePosition, getVehicleId, routeDetails]);
  
  // Handle dismissing itinerary change toast
  const handleDismissChangeToast = useCallback(() => {
    setItineraryChangeToast(null);
  }, []);
  
  if (!routeDetails) {
    return (
      <div className="flex h-full min-h-screen items-center justify-center bg-slate-100 px-4">
        <div className="max-w-md rounded-lg bg-white p-6 text-center shadow">
          <h2 className="text-lg font-semibold text-slate-900">No Route Assigned</h2>
          <p className="mt-2 text-sm text-slate-600">
            We couldn&apos;t find any itinerary data for the current driver.
          </p>
          {typeof onBack === 'function' && (
            <button
              onClick={onBack}
              className="mt-4 inline-flex items-center rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
            >
              Back
            </button>
          )}
        </div>
      </div>
    );
  }
  
  if (loading) {
    return <div>Loading...</div>;
  }

  // Layout styles for portrait/landscape
  const mapSectionStyle = isPhone
    ? { minHeight: '33vh', padding: '5px', display: 'flex', justifyContent: 'center', alignItems: 'center' }
    : { padding: '5px', flex: '1', display: 'flex', justifyContent: 'center', alignItems: 'center' };

  const currentWaypointStyle = isPhone
    ? { flexShrink: 0 }
    : { flexShrink: 0 };

  const itinerarySectionStyle = isPhone
    ? { flex: '1', overflowY: 'auto' }
    : { flex: '1', overflowY: 'auto' };

  return (
    <div className="bg-slate-100" style={{ height: `var(--app-content-height)` }}>
      {/* Connection error toast */}
      {connectionError && (
        <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-50 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg text-sm">
          {connectionError}
        </div>
      )}
      
      {/* Itinerary change toast - persistent until dismissed */}
      {itineraryChangeToast && (
        <div className="fixed top-16 left-1/2 transform -translate-x-1/2 z-[1200] bg-amber-500 text-white px-4 py-3 rounded-lg shadow-lg max-w-sm">
          <div className="flex items-center justify-between">
            <div className="flex-1 mr-4">
              <p className="font-semibold">Schedule Changed</p>
              <p className="text-sm">
                {itineraryChangeToast.added > 0 && `${itineraryChangeToast.added} trip${itineraryChangeToast.added > 1 ? 's' : ''} added`}
                {itineraryChangeToast.added > 0 && itineraryChangeToast.removed > 0 && ', '}
                {itineraryChangeToast.removed > 0 && `${itineraryChangeToast.removed} trip${itineraryChangeToast.removed > 1 ? 's' : ''} removed`}
                {itineraryChangeToast.updated && itineraryChangeToast.added === 0 && itineraryChangeToast.removed === 0 && 'Your schedule has been updated'}
              </p>
            </div>
            <button
              onClick={handleDismissChangeToast}
              className="bg-white text-amber-600 font-bold px-3 py-1 rounded hover:bg-amber-100 transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )}
      
      <GeolocationHandler
        onLocationUpdate={handleLocationUpdate}
        setIsAccurate={setIsAccurate}
        vehicleId={getVehicleId()}
        fleetSettings={fleetSettings}
      />

      {/* Metered trip: final fare → PRICE|Ok before the payment sheet. */}
      <ItineraryFinalFareModal
        open={!!fareFinalizeWaypoint}
        waypoint={fareFinalizeWaypoint}
        pending={fareFinalize.status === 'pending'}
        error={fareFinalize.status === 'error' ? fareFinalize.error : null}
        onConfirm={handleFinalizeFare}
        onCancel={closeFareFinalize}
      />

      {/* Collect Payment — same sheet as Live Dispatch, Itinerary completion contract. */}
      <PaymentCollectionSheet
        open={!!sheetWaypoint}
        onClose={closePaymentSheet}
        tripId={sheetWaypoint ? waypointTripId(sheetWaypoint) : null}
        tripNumber={sheetWaypoint?.trip?.reservation?.number || sheetWaypoint?.tripNo || sheetWaypoint?.TripNo}
        fleetCode={fleetSettings?.unitId || fleetSettings?.fleetCode || localStorage.getItem('fleetCode') || ''}
        riderAppUrl={fleetSettings?.riderAppUrl}
        payment={sheetWaypoint ? paymentBlockFor(sheetWaypoint) : null}
        priceItems={sheetWaypoint?.trip?.reservation?.priceItems}
        paymentStatus={sheetWaypoint ? paymentStatusFor(sheetWaypoint) : null}
        onCollectCash={handleCollectCash}
        cashPending={cashState.pending}
        cashError={cashState.error}
        onRequestHostedPage={handleRequestHostedPage}
        otpDialogue={otpDialogue}
        onSelectFare={handleSelectFare}
        passengerVerified={
          !!sheetWaypoint
          && tripIdKey(verifiedTripId) === tripIdKey(waypointTripId(sheetWaypoint))
        }
        collectionUrl={collectionForSheet ? collectionState.url : null}
        collectionError={collectionForSheet ? collectionState.error : null}
        onViewReceipt={handleViewReceipt}
        onTextReceipt={handleTextReceipt}
        receipt={receiptState}
        // Header/footer identity for a receipt the driver saves locally.
        fleetName={
          fleetSettings?.fleetName
          || fleetSettings?.siteName
          || fleetSettings?.unitId
          || localStorage.getItem('fleetCode')
          || ''
        }
        driverName={userSettings?.Name || userSettings?.name || ''}
        onCompleteTrip={handleCompleteFromSheet}
        canCompleteNow={paymentSheet.source === 'endTrip'}
      />

      {/* Marketplace entry point. Itinerary supports Marketplace the same as Live
          dispatch (which offers it as "Available Trips" on its own no-work screen), but
          an itinerary driver who never visits the Dashboard had no way in. The server
          decides what is claimable — this only opens the shared screen.
          On-Demand offers are deliberately NOT here: those are Live-dispatch only. */}
      {marketplaceAvailable && typeof navigateTo === 'function' && (
        <div className="flex justify-end px-3 py-2">
          <button
            type="button"
            onClick={() => navigateTo('marketplace')}
            className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-800"
            data-testid="itinerary-browse-marketplace"
          >
            Available Trips
          </button>
        </div>
      )}

      {googleApiKey && center && (
        <div className={isPhone ? 'flex flex-col h-full' : 'flex flex-row h-full'}>
          {!isPhone ? (
            /* Desktop/Landscape layout: map on left, currentwaypoint + itinerary on right */
            <>
              {/* Map section - full left side */}
              <div ref={mapContainerRef} style={mapSectionStyle}>
                <ItineraryMap
                  googleApiKey={googleApiKey}
                  isPhone={isPhone}
                  center={center}
                  vehiclePosition={vehiclePosition}
                  currentWaypoint={currentWaypoint}
                  schedule={schedule}
                  isAccurate={isAccurate}
                  onMapLoad={onLoad}
                  fleetSettings={fleetSettings}
                />
              </div>

              {/* Right side - CurrentWaypoint + Itinerary */}
              <div className="flex flex-col" style={{ flex: '1', overflowY: 'auto' }}>
                {currentWaypoint && schedule.length > 0 && (
                  <div style={currentWaypointStyle}>
                    <ItineraryCurrentWaypoint
                      waypoint={currentWaypoint}
                      tripPaidLive={isCurrentTripPaidLive}
                      tripPaymentOverride={currentOverride}
                      tripNeedsReview={currentNeedsReview}
                      onOpenPayment={openPaymentSheet}
                      expectedTime={null}
                      isAccurate={isAccurate}
                      isPhone={isPhone}
                      currentLocation={vehiclePosition}
                      onShiftComplete={() => navigateTo('completeShift')}
                      activeBreak={activeBreak}
                    />
                  </div>
                )}
                {schedule.length > 1 && (
                  <div style={itinerarySectionStyle}>
                    <ItineraryItinerary
                      isPhone={isPhone}
                      schedule={schedule.slice(1)}
                      currentWaypoint={currentWaypoint}
                      vehicleLocation={vehiclePosition}
                      fleetSettings={fleetSettings}
                      googleMapsLoaded={!!googleApiKey}
                      routeDetails={routeDetails}
                      onStartBreak={handleStartBreak}
                      onRequestSplitShift={handleRequestSplitShift}
                      canFlag={canFlag}
                      onFlagClick={openFlagTrip}
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Mobile/Portrait layout: map at top (33vh), currentwaypoint (350px), then itinerary (rest) */
            <>
              {/* Map section - 33vh minimum */}
              <div ref={mapContainerRef} style={mapSectionStyle}>
                <ItineraryMap
                  googleApiKey={googleApiKey}
                  isPhone={isPhone}
                  center={center}
                  vehiclePosition={vehiclePosition}
                  currentWaypoint={currentWaypoint}
                  schedule={schedule}
                  isAccurate={isAccurate}
                  onMapLoad={onLoad}
                  fleetSettings={fleetSettings}
                />
              </div>

              {/* CurrentWaypoint section - fixed 350px */}
              {currentWaypoint && schedule.length > 0 && (
                <div style={currentWaypointStyle}>
                  <ItineraryCurrentWaypoint
                    waypoint={currentWaypoint}
                    tripPaidLive={isCurrentTripPaidLive}
                    tripPaymentOverride={currentOverride}
                    tripNeedsReview={currentNeedsReview}
                    onOpenPayment={openPaymentSheet}
                    expectedTime={null}
                    isAccurate={isAccurate}
                    isPhone={isPhone}
                    currentLocation={vehiclePosition}
                    onShiftComplete={() => navigateTo('completeShift')}
                    activeBreak={activeBreak}
                  />
                </div>
              )}

              {/* Itinerary section - rest of viewport */}
              {schedule.length > 1 && (
                <div style={itinerarySectionStyle}>
                  <ItineraryItinerary
                    isPhone={isPhone}
                    schedule={schedule.slice(1)}
                    currentWaypoint={currentWaypoint}
                    vehicleLocation={vehiclePosition}
                    fleetSettings={fleetSettings}
                    googleMapsLoaded={!!googleApiKey}
                    routeDetails={routeDetails}
                    onStartBreak={handleStartBreak}
                    onRequestSplitShift={handleRequestSplitShift}
                    canFlag={canFlag}
                    onFlagClick={openFlagTrip}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!isAccurate && (
        <div className="fixed top-16 left-0 mt-1 p-1 z-[1200] rounded-tr rounded-br bg-red-500 text-white opacity-50">
          <span className="text-xs">!GPS</span>
        </div>
      )}

      {/* Phase B — itinerary-mode flag trip */}
      <FlagTripCreateModal
        isOpen={showFlagTripModal}
        onClose={() => setShowFlagTripModal(false)}
        routeId={routeDetails?.routeId}
        vehicleId={getVehicleId()}
        driverId={driverData?.dispatchData?.driverId}
        currentLocation={vehiclePosition}
        fleetSettings={fleetSettings}
        googleMapsLoaded={!!googleApiKey}
        onSuccess={() => setShowFlagTripModal(false)}
      />
    </div>
  );
}

export default ItineraryDispatch;
